import { CRUDInputElementConstants } from "@kuika/kuika-cl-model";

/**
 * One selectable UI element for a form field (e.g. "Text Input", "Checkbox").
 * Mirrors the shape the legacy form wizard uses so the Forms tab stays consistent.
 */
export interface IElementTypeOption {
  /** The persisted element identifier (a CRUDInputElementConstants value). */
  name: string;
  /** Human-facing label shown in the dropdown. */
  label: string;
  /** True when the element renders on web only and must be hidden for mobile projects. */
  isWebOnly: boolean;
}

/**
 * Buckets a raw parameter/column data type into the coarse family the element list keys off.
 * Case- and separator-insensitive so backend spellings ("DateTimeOffset") and the legacy
 * wizard's ("DatetimeOffset") land on the same family.
 */
type TypeFamily = "boolean" | "date" | "datetime" | "numeric" | "string" | "guid" | "image" | "signature" | "unknown";

const resolveFamily = (typeName: string): TypeFamily => {
  const key: string = (typeName ?? "").toLowerCase();
  if (key === "boolean") return "boolean";
  if (key === "date") return "date";
  if (key === "datetime" || key === "datetimeoffset") return "datetime";
  if (
    key === "decimal" ||
    key === "double" ||
    key === "single" ||
    key === "int16" ||
    key === "int32" ||
    key === "int64" ||
    key === "byte"
  ) {
    return "numeric";
  }
  if (key === "string") return "string";
  if (key === "guid") return "guid";
  if (key === "image") return "image";
  if (key === "signature") return "signature";
  return "unknown";
};

/**
 * The Select / MultiSelect pair is offered for every simple scalar (not booleans, dates or
 * media), and is the *only* option for a type that is itself a database table (lookup).
 */
const selectPair = (): IElementTypeOption[] => [
  { name: CRUDInputElementConstants.Select, label: "SelectBox", isWebOnly: false },
  { name: CRUDInputElementConstants.MultiSelect, label: "MultiSelect", isWebOnly: true }
];

/**
 * The set of element types a field of the given data type may use. Faithfully replicates the
 * legacy wizard's mapping (`form-wizard-custom.getElementTypeOptions`) as a pure function so
 * both surfaces stay in lockstep.
 *
 * @param typeName        The field's data type (parameter data type or column type).
 * @param tableTypeNames  Fully-qualified table type names (`schema.name`); a type in this set
 *                        is a lookup and only supports Select / MultiSelect.
 * @param isMobileProject When true, web-only elements are filtered out.
 */
export const getElementTypeOptions = (
  typeName: string,
  tableTypeNames: readonly string[] = [],
  isMobileProject: boolean = false
): IElementTypeOption[] => {
  if (!typeName) return [];

  const family: TypeFamily = resolveFamily(typeName);
  const pair: IElementTypeOption[] =
    family === "boolean" || family === "date" || family === "datetime" || family === "image" || family === "signature"
      ? []
      : selectPair();

  if (tableTypeNames.includes(typeName)) {
    return filterForProject(pair, isMobileProject);
  }

  let options: IElementTypeOption[];
  switch (family) {
    case "boolean":
      options = [
        { name: CRUDInputElementConstants.Checkbox, label: "Checkbox", isWebOnly: false },
        { name: CRUDInputElementConstants.ToggleSwitch, label: "Switch", isWebOnly: false }
      ];
      break;
    case "date":
      options = [
        { name: CRUDInputElementConstants.PickerDate, label: "Date", isWebOnly: false },
        { name: CRUDInputElementConstants.PickerDateTime, label: "DateTime", isWebOnly: false }
      ];
      break;
    case "datetime":
      options = [
        { name: CRUDInputElementConstants.PickerDateTime, label: "DateTime", isWebOnly: false },
        { name: CRUDInputElementConstants.PickerDate, label: "Date", isWebOnly: false }
      ];
      break;
    case "numeric":
      options = [
        { name: CRUDInputElementConstants.InputNumeric, label: "NumberInput", isWebOnly: false },
        { name: CRUDInputElementConstants.Currency, label: "Currency", isWebOnly: true },
        { name: CRUDInputElementConstants.Percent, label: "Percent", isWebOnly: true }
      ];
      break;
    case "string":
      options = [
        { name: CRUDInputElementConstants.TextInput, label: "TextInput", isWebOnly: false },
        { name: CRUDInputElementConstants.TextArea, label: "TextArea", isWebOnly: false },
        { name: CRUDInputElementConstants.InputNumeric, label: "NumberInput", isWebOnly: false },
        { name: CRUDInputElementConstants.Password, label: "Password", isWebOnly: false },
        { name: CRUDInputElementConstants.Email, label: "Email", isWebOnly: false },
        { name: CRUDInputElementConstants.Url, label: "Url", isWebOnly: false }
      ];
      break;
    case "guid":
      options = [
        { name: CRUDInputElementConstants.TextInput, label: "TextInput", isWebOnly: false },
        { name: CRUDInputElementConstants.TextArea, label: "TextArea", isWebOnly: false }
      ];
      break;
    case "image":
      options = [{ name: CRUDInputElementConstants.ImageView, label: "Image", isWebOnly: false }];
      break;
    case "signature":
      options = [{ name: CRUDInputElementConstants.Signature, label: "Signature", isWebOnly: false }];
      break;
    default:
      options = [];
      break;
  }

  options.push(...pair);
  return filterForProject(options, isMobileProject);
};

const filterForProject = (options: IElementTypeOption[], isMobileProject: boolean): IElementTypeOption[] =>
  isMobileProject ? options.filter((option: IElementTypeOption): boolean => !option.isWebOnly) : options;

/** The element type a newly added field defaults to: the first valid option for its data type. */
export const defaultElementTypeFor = (typeName: string, tableTypeNames: readonly string[] = []): string => {
  const options: IElementTypeOption[] = getElementTypeOptions(typeName, tableTypeNames);
  return options[0]?.name ?? "";
};

/** A selectable base data type for a custom (unbound) form field. */
export interface IFieldTypeOption {
  value: string;
  label: string;
}

/**
 * The data types a custom field may take. Values are the backend type spellings the element-type
 * resolver understands (see {@link getElementTypeOptions}); labels are user-facing.
 */
export const CUSTOM_FIELD_TYPES: IFieldTypeOption[] = [
  { value: "String", label: "Text" },
  { value: "Int32", label: "Number" },
  { value: "Decimal", label: "Decimal" },
  { value: "Boolean", label: "Boolean" },
  { value: "Date", label: "Date" },
  { value: "DateTime", label: "Date & Time" },
  { value: "Guid", label: "Guid" },
  { value: "Image", label: "Image" }
];

/** The dropdown label for an element type value, falling back to the raw value when unknown. */
export const elementTypeLabel = (elementType: string, typeName: string): string => {
  const match: IElementTypeOption | undefined = getElementTypeOptions(typeName).find(
    (option: IElementTypeOption): boolean => option.name === elementType
  );
  return match?.label ?? elementType;
};
